// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { useScreenWidth } from '@tectonics/hooks';
import { intersect } from '@tectonics/utils/arrays';
import { getSupportedTraits } from '@tectonics/prs/feature';
import { getSafeSupportedFeatureByName } from '@tectonics/prs/support';
import { getCountry, useOpenedIncomingPayment } from '@tectonics/prs/incoming-payment';
import { getDebitCardBrandsForCountry, getDebitCardBrandImageSrc } from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/commons/debit-card-brands';

import lookAndFeel from '@tectonics/look-and-feel/volcanes';

export function DisplayDebitcardBrands(): Node {
  const incomingPayment = useOpenedIncomingPayment();
  const country = getCountry(incomingPayment);
  const screenWidth = useScreenWidth();

  const allowedBrandsForCountry = getDebitCardBrandsForCountry(country);

  const displayDebitCardBrandFeatureName = 'display_debit_card_accepts_brand';
  const supportedDisplayDebitCardBrandFeature = getSafeSupportedFeatureByName(
    displayDebitCardBrandFeatureName,
    lookAndFeel.prsSupport
  );

  const supportedBrands = getSupportedTraits(supportedDisplayDebitCardBrandFeature);
  const availableBrands = intersect(supportedBrands, allowedBrandsForCountry);

  const amountOfBrandsToDisplay = screenWidth < 420 ? 5 : 6;
  const shouldDisplayMoreBrandsBadge = availableBrands.length > amountOfBrandsToDisplay;
  const sliceIndex = shouldDisplayMoreBrandsBadge ? amountOfBrandsToDisplay - 1 : amountOfBrandsToDisplay;
  const brandsToDisplay = availableBrands.slice(0, sliceIndex);

  return (
    <Root>
      {brandsToDisplay.map(brand => (
        <CardBrandWrapper key={brand}>
          <CardBrandImage src={getDebitCardBrandImageSrc(brand)} alt={brand} />
        </CardBrandWrapper>
      ))}
      {shouldDisplayMoreBrandsBadge &&
        <CardBrandWrapper>
          <MoreBrandsBadge>
            +{availableBrands.length - sliceIndex}
          </MoreBrandsBadge>
        </CardBrandWrapper>
      }
    </Root>
  );
}

const Root = styled.div`
  height: 100%;
  display: flex;
`;

const CardBrandWrapper = styled.div`
  flex-grow: 1;
  padding-left: 4px;
`;

const CardBrandImage = styled.img`
  height: 100%;
  width: 100%;
`;
// @TODO: Discuss the color change on more brands with Bel
const MoreBrandsBadge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 0 5px;
  color: ${props => props.theme.colors.neutral[700]};
  font-size: 12px;
  font-weight: bold;
  border-radius: 2px;
  background-color: ${props => props.theme.colors.neutral[100]};
  font-family: ${props => props.theme.fontFamily.display};
`;
